import { useCallback, useMemo } from "react";

import { useQueryClient } from "@tanstack/react-query";

import type { LeadsSearchResponseDto } from "app/pages/sales-intelligence/modules/leads-search";
import { getMainCompanyDomainOrDefault } from "app/pages/sales-intelligence/modules/leads-search/utils/rows";
import type { CompanySelectObject } from "app/pages/sales-intelligence/modules/leads-search-filters/types/description.dto";
import { opportunityListQueryKeyFactory } from "app/pages/sales-intelligence/pages/opportunity-list/query/queryKeyFactory";

import { useListParams } from "../context/ListParamsContext";

export const useCompaniesListData = () => {
    const queryClient = useQueryClient();

    const listParams = useListParams();

    const getData = useCallback((): CompanySelectObject[] => {
        const matchingQueries = queryClient.getQueryCache().findAll({
            queryKey: opportunityListQueryKeyFactory.leadsTableData(listParams.id, "companies"),
            exact: false,
        });

        const latestQuery = matchingQueries.length
            ? matchingQueries.reduce((latest, q) => {
                  const latestTs = latest.state.dataUpdatedAt ?? 0;
                  const qTs = q.state.dataUpdatedAt ?? 0;
                  return qTs > latestTs ? q : latest;
              })
            : undefined;

        const latestQueryData: LeadsSearchResponseDto = latestQuery?.state.data as LeadsSearchResponseDto;

        return (
            latestQueryData?.rows?.map((row) => {
                const domain = getMainCompanyDomainOrDefault(row).domain;
                return { company: row.company ?? domain, domain, icon: row.favicon };
            }) || []
        );
    }, [queryClient, listParams.id]);

    const data = useMemo(() => getData(), [getData]);

    return { data, getData };
};
